import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import useFetchAction from '../../../hooks/useFetchAction';
import { getAnswer as getAnswerAction } from '../../../store/actions/answer';
import { useParams } from 'react-router';
import Spinner from '../../atoms/Spinner/Spinner';
// eslint-disable-next-line @stylistic/max-len
import ContributeAnswerFormTemplate from '../../templates/ContributeAnswerFormTemplate/ContributeAnswerFormTemplate';
// eslint-disable-next-line @stylistic/max-len
import ErrorContributeTemplate from '../../templates/ErrorContributeTemplate/ErrorContributeTemplate';
// eslint-disable-next-line @stylistic/max-len
import ContributeAnswerDetailTemplate from '../../templates/ContributeAnswerDetailTemplate/ContributeAnswerDetailTemplate';
// eslint-disable-next-line @stylistic/max-len
import ContributeCloseButtonTemplate from '../../templates/ContributeCloseButtonTemplate/ContributeCloseButtonTemplate';
import ContributeAnswer from '../../organisms/ContributeAnswer/ContributeAnswer';
import { getFormTask as getFormTaskAction } from '../../../store/actions/task';

/*
 * READ-ONLY consultation of an answer that was already recorded.
 * "answerOf" says which object the answer belongs to. It is set by the route that
 * mounts this page (see App.jsx):
 *   task          - one contributor's own work. In v5 that object is called a hit.
 *   taskLine      - the answer finally retained for the shared work item. v5 only.
 *   certification - a candidate's answer to one question of their certification test.
 */
const ContributeAnwserPage = ({ withMarking, answerOf = 'task' }) => {
  const params = useParams();
  const [getAnswer, answerLoading, answerError, answer] = useFetchAction(
    getAnswerAction,
  );
  const [getForm, formLoading, formError, form] = useFetchAction(getFormTaskAction);

  // Creates a key-value pair of task data
  const formData = useMemo(() => {
    let formData = { ...answer?.json };

    answer?.columns.forEach((col) => {
      if (formData?.data?.find((elem) => elem?.id === col?.name) == null) {
        if (formData?.data?.length) {
          formData?.data.push({
            id: col.name,
            type: col.type,
            value: col.data,
          });
        } else {
          formData = {
            ...formData,
            data: [
              {
                id: col.name,
                type: col.type,
                value: col.data,
              },
            ],
          };
        }
      }
    });

    return formData;
  }, [answer]);

  const columnAnswer = useMemo(() =>
    (answer && answer.Columns)
      ? answer.Columns.map((elem) => {
        const tempObj = {
          id: elem.Name,
          type: elem.Type,
          value: elem.Data,
        };
        return tempObj;
      })
      : []
  , [answer]);

  // First we load the task
  useEffect(() => {
    getAnswer(answerOf, params);
  }, []);

  // Once task is loaded, load the form
  useEffect(() => {
    if (answer?.formId && !form && !formLoading) {
      getForm(answer.formId);
    }
  }, [answer, form, formLoading]);

  if (!answerError && !formError) {
    if (!form || formLoading || answerLoading) {
      return <Spinner />;
    }
  }

  const emptyAnswer = { blocks: [], data: columnAnswer, fields: [] };

  return (
    <ContributeAnswer>
      <ContributeCloseButtonTemplate
        isView
        projectId={answer.projectId ?? answer.certificationId}
      />
      {withMarking && (
        <ContributeAnswerDetailTemplate
          answer={answer}
          form={form}
        ></ContributeAnswerDetailTemplate>
      )}
      <ErrorContributeTemplate answerError={answerError} formError={formError}>
        <ContributeAnswerFormTemplate
          answer={formData ? formData : emptyAnswer}
          form={form}
        />
      </ErrorContributeTemplate>
    </ContributeAnswer>
  );
};

ContributeAnwserPage.propTypes = {
  withMarking: PropTypes.bool,
  answerOf: PropTypes.oneOf(['task', 'taskLine', 'certification']),
};

export default ContributeAnwserPage;
